import type {CSSProperties} from 'react';
import {radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeNotifLook = 'notif-chip' | 'notif-chip-strong' | 'notif-action' | 'notif-main' | 'notif-main-idle' | 'notif-trailing' | 'notif-more' | 'notif-more-open' | 'notif-clear' | 'notif-clear-confirm' | 'notif-section-action';

const NOTIF_LOOKS = new Set<string>(['notif-chip', 'notif-chip-strong', 'notif-action', 'notif-main', 'notif-main-idle', 'notif-trailing', 'notif-more', 'notif-more-open', 'notif-clear', 'notif-clear-confirm', 'notif-section-action']);
export const isWedgeNotifLook = (look: string): look is WedgeNotifLook => NOTIF_LOOKS.has(look);

/* Released notification row controls of the Wedge notification center: the action chip of the expand strip, the swipe action, the row
   face button, the trailing text action and the overflow button. Every box here is a shipped inline style, off-scale numbers included;
   each color reads the exact legacy role, so the shipped ink rules keep matching.
   The chip is an inline-flex pill (6px gap, 34px high at least, 12px side padding, 12.5px type at weight 650) with a 1px border in the
   bold separator, no fill and the label ink. Strong, it takes a transparent border, the accent fill and the on-accent ink. A caller
   tone replaces the ink (plain) or the fill (strong). The swipe action is a 74px column (3px gap, centered, 11.5px bold type, no border);
   its fill and ink are the caller's, because the wrapper owns the gold, red or tone pair. The row face button is a flex-1 row (12px gap,
   no padding, left-aligned, the ink of its parent) with a pointer cursor, or the default cursor when the row has no open action. The
   trailing action is a 36px high text button (6px side padding, 12.5px bold type, nowrap) in the accent ink. The overflow button is a
   32px by 44px box (10px radius, 2px margin right) in the tertiary ink, on the alternate surface while the strip is open.
   The chip, the face button, the trailing action and the overflow button keep touch-action manipulation.
   The Clear all button of the center header is a 36px high pill (12px side padding, 12.5px bold type, 1px border in the bold separator,
   no fill, the secondary ink); confirming, it takes the red border and fill, and the wrapper passes the literal white ink, which no token
   carries. The section action is a 32px high text button (4px side padding, 12px bold type, no border, no fill) in the accent ink.
   No notification look draws a transparent target (wedgeNotifDrawsHit), as legacy. The chips are 34px high and wrap 8px apart, so a
   44px region would reach 5px past each edge and overlap the next chip. The swipe action and the row face button are 60px tall or
   more, so a region could only repeat the box. The trailing action (36px) and the overflow button (32px wide) sit in one row with the
   face button on their left and 2px of margin on their right, so their regions would overlap a neighbor. The raster effect of an
   anchor was not measured in a browser. */
const TOUCH = {touchAction: 'manipulation'} as const;

export const wedgeNotifDrawsHit = (look: string): boolean => !look.startsWith('notif-');

export function wedgeNotifStyle(look: WedgeNotifLook): CSSProperties {
  const r = wedgeExactRole, fontFamily = r('font');
  switch (look) {
    case 'notif-chip':
    case 'notif-chip-strong': {
      const strong = look === 'notif-chip-strong';
      return {
        display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 34, padding: '0 12px', borderRadius: radius.pill,
        border: `1px solid ${strong ? 'transparent' : r('separatorBold')}`, background: strong ? r('accent') : 'transparent',
        color: strong ? r('onAccent') : r('label'), fontFamily, fontSize: 12.5, fontWeight: 650, cursor: 'pointer', whiteSpace: 'nowrap', ...TOUCH,
      };
    }
    case 'notif-action': return {
      width: 74, border: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
      fontFamily, fontSize: 11.5, fontWeight: weight.bold,
    };
    case 'notif-main':
    case 'notif-main-idle': return {
      flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 0, padding: 0, textAlign: 'left', fontFamily,
      cursor: look === 'notif-main' ? 'pointer' : 'default', color: 'inherit', ...TOUCH,
    };
    case 'notif-trailing': return {
      flexShrink: 0, minHeight: 36, padding: '0 6px', background: 'none', border: 0, cursor: 'pointer', fontFamily, fontSize: 12.5,
      fontWeight: weight.bold, color: r('accent'), whiteSpace: 'nowrap', ...TOUCH,
    };
    case 'notif-more':
    case 'notif-more-open': return {
      flexShrink: 0, width: 32, height: 44, marginRight: 2, border: 0, borderRadius: 10, cursor: 'pointer',
      background: look === 'notif-more-open' ? r('surfaceAlt') : 'transparent', color: r('labelTer'),
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...TOUCH,
    };
    case 'notif-clear':
    case 'notif-clear-confirm': {
      const confirm = look === 'notif-clear-confirm';
      return {
        minHeight: 36, padding: '0 12px', borderRadius: radius.pill, cursor: 'pointer', border: `1px solid ${confirm ? r('red') : r('separatorBold')}`,
        background: confirm ? r('red') : 'transparent', color: r('labelSec'), fontFamily, fontSize: 12.5, fontWeight: weight.bold, ...TOUCH,
      };
    }
    case 'notif-section-action': return {
      minHeight: 32, padding: '0 4px', background: 'none', border: 0, cursor: 'pointer', fontFamily, fontSize: 12, fontWeight: weight.bold, color: r('accent'),
    };
  }
}
